import { useState, Fragment, useEffect } from "react";
import { Link, useSearch } from "wouter";
import { useGetBusinesses } from "@workspace/api-client-react";
import { GoogleFeedAd } from "@/components/GoogleAd";
import { QueryState } from "@/components/QueryState";
import { useSEO } from "@/hooks/use-seo";
import { Input } from "@/components/ui/input";
import { useTown } from "@/hooks/use-town";
import { useCategories } from "@/hooks/use-categories";
import { useAnalytics } from "@/hooks/use-analytics";
import { SidebarFilter } from "@/components/SidebarFilter";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <svg key={star} className={`w-3 h-3 ${star <= Math.round(rating) ? "text-secondary fill-secondary" : "text-slate-300 fill-slate-300"}`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {rating > 0 && <span className="text-xs text-slate-500 ml-1">{rating.toFixed(1)}</span>}
    </div>
  );
}

function BusinessesSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[1,2,3,4,5,6].map(i => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
          <div className="h-40 bg-slate-200" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-3 bg-slate-200 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Businesses() {
  useSEO({ title: "Ayrshire Business Directory", description: "Find trusted local businesses across Ayrshire — plumbers, electricians, restaurants, shops, beauty salons and more in Ayr, Kilmarnock, Irvine and beyond." });
  const { track } = useAnalytics();
  useEffect(() => { track("page_viewed", { screen: "businesses" }); }, []);
  const { townParam } = useTown();
  const qs = useSearch();
  const initialSearch = new URLSearchParams(qs).get("search") ?? "";
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState("All");

  const { data, isLoading, isError, refetch } = useGetBusinesses({
    town: townParam,
    category: category === "All" ? undefined : category,
    search: search || undefined,
  });

  const businesses = data?.items ?? [];
  const categories = ["All", ...useCategories("business")];

  return (
    <div className="min-h-screen bg-slate-50">

      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-5">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Support local</p>
              <h1 className="text-2xl font-bold text-primary">Business Directory</h1>
            </div>
          </div>
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
            <Input className="pl-9" placeholder="Search businesses, categories..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <SidebarFilter items={categories} selected={category} onSelect={setCategory}>
        <div className="px-4 py-5">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={businesses.length === 0}
          onRetry={refetch}
          skeleton={<BusinessesSkeleton />}
          errorTitle="Couldn't load the directory"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          emptyIcon="🏪"
          emptyTitle={search ? "No businesses match your search." : "No businesses listed yet."}
          emptyBody={search ? "Try a different search term, town or category." : "Check back soon — new local businesses are added regularly."}
          emptyAction={search ? { label: "Clear search", onClick: () => setSearch("") } : undefined}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {businesses.map((biz, i) => {
              const isOrderStore = (biz as unknown as { fromOrderModule?: boolean }).fromOrderModule;
              const orderSlug = (biz as unknown as { orderStoreSlug?: string | null }).orderStoreSlug;
              const href = isOrderStore && orderSlug ? `/order/${orderSlug}` : `/businesses/${biz.slug ?? biz.id}`;
              return (
              <Fragment key={`${isOrderStore ? "os" : "biz"}-${biz.id}`}>
              <Link
                href={href}
                className="group block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                {biz.imageUrl ? (
                  <div className="h-40 overflow-hidden">
                    <img src={biz.imageUrl} alt={biz.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                ) : (
                  <div className="h-40 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                    <span className="text-5xl">{isOrderStore ? "🛒" : "🏪"}</span>
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-bold text-slate-800 line-clamp-1 group-hover:text-primary transition-colors">{biz.name}</h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isOrderStore && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                          🛒 Order
                        </span>
                      )}
                      {biz.verified && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                          <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
                    <span className="font-medium text-slate-600">{biz.category}</span>
                    <span>·</span>
                    <span>📍 {biz.town}</span>
                  </div>
                  {biz.rating > 0 && (
                    <div className="mb-2 flex items-center gap-2">
                      <StarRating rating={biz.rating} />
                      {biz.reviewCount > 0 && <span className="text-xs text-slate-400">({biz.reviewCount})</span>}
                    </div>
                  )}
                  {biz.description && (
                    <p className="text-slate-500 text-sm line-clamp-2">{biz.description}</p>
                  )}
                </div>
              </Link>
              {i === 2 && <div className="col-span-full"><GoogleFeedAd /></div>}
              </Fragment>
              );
            })}
          </div>
        </QueryState>
        </div>
      </SidebarFilter>
    </div>
  );
}
